const EXT_COLORS: Record<string, string> = {
  csv: "#5a8f4a",
  xlsx: "#217346",
  xls: "#217346",
  png: "#8b5fbf",
  jpg: "#8b5fbf",
  jpeg: "#8b5fbf",
  gif: "#8b5fbf",
  svg: "#8b5fbf",
  webp: "#8b5fbf",
  ts: "#3178c6",
  tsx: "#3178c6",
  js: "#f5a623",
  jsx: "#f5a623",
  py: "#3776ab",
  rb: "#c62828",
  go: "#00add8",
  rs: "#c4775a",
  md: "#7a6855",
  txt: "#888888",
  log: "#888888",
  db: "#c06a20",
  sqlite: "#c06a20",
  sql: "#c06a20",
  pdf: "#c62828",
  html: "#e44d26",
  css: "#3178c6",
  json: "#f5a623",
  yaml: "#7a6855",
  yml: "#7a6855",
  toml: "#7a6855",
  sh: "#333333",
  bash: "#333333",
};

function extColor(name: string): string {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  return EXT_COLORS[ext] ?? "#888888";
}

function extLabel(name: string): string {
  const ext = name.split(".").pop()?.toUpperCase() ?? "?";
  return ext.length > 4 ? ext.slice(0, 3) : ext;
}

export function FileIcon({
  name,
  className,
}: {
  name: string;
  className?: string;
}) {
  return (
    <div
      className={`inline-flex flex-col items-center gap-[2px] w-[38px] cursor-pointer group ${className ?? ""}`}
    >
      <div className="relative w-[26px] h-[32px] rounded-[3px] border border-[var(--blt)] bg-[var(--white)] flex items-center justify-center transition-all group-hover:border-[var(--accent)] group-hover:-translate-y-[1px]">
        <div className="absolute top-0 right-0 w-[6px] h-[6px] bg-[var(--bg)] border-l border-b border-[var(--blt)] rounded-bl-[2px]" />
        <span
          className="text-[6px] font-bold uppercase tracking-wide text-white px-[2px] rounded-[1px]"
          style={{
            fontFamily: "var(--font-mono)",
            backgroundColor: extColor(name),
          }}
        >
          {extLabel(name)}
        </span>
      </div>
      <span
        className="text-[7px] text-[var(--t2)] max-w-[38px] text-center leading-[1.25]"
        style={{
          fontFamily: "var(--font-mono)",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical" as any,
          overflow: "hidden",
          wordBreak: "break-all" as any,
        }}
      >
        {name}
      </span>
    </div>
  );
}

export function FolderIcon({
  name,
  count,
  className,
}: {
  name: string;
  count?: number;
  className?: string;
}) {
  return (
    <div
      className={`inline-flex flex-col items-center gap-[2px] w-[38px] cursor-pointer group ${className ?? ""}`}
    >
      <div className="relative w-[26px] h-[32px] rounded-[3px] border border-[var(--blt)] bg-[var(--white)] flex items-center justify-center transition-all group-hover:border-[var(--accent)]">
        <div className="absolute -top-[1px] left-[2px] w-[10px] h-[4px] bg-[var(--white)] border border-b-0 border-[var(--blt)] rounded-t-[2px]" />
        {count != null && (
          <span
            className="text-[8px] text-[var(--t3)]"
            style={{ fontFamily: "var(--font-mono)" }}
          >
            {count}
          </span>
        )}
      </div>
      <span
        className="text-[7px] text-[var(--t2)] max-w-[38px] text-center leading-[1.25]"
        style={{
          fontFamily: "var(--font-mono)",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical" as any,
          overflow: "hidden",
          wordBreak: "break-all" as any,
        }}
      >
        {name}
      </span>
    </div>
  );
}

export function MoreFilesIcon({ count }: { count: number }) {
  return (
    <div className="inline-flex flex-col items-center gap-[2px] w-[38px]">
      <div className="w-[26px] h-[32px] rounded-[3px] border border-dashed border-[var(--blt)] flex items-center justify-center text-[8px] text-[var(--t4)]">
        +{count}
      </div>
      <span
        className="text-[8px] text-[var(--t4)]"
        style={{ fontFamily: "var(--font-mono)" }}
      >
        more
      </span>
    </div>
  );
}

export function OutputFileChip({
  name,
  dark,
}: {
  name: string;
  dark?: boolean;
}) {
  return (
    <div className="inline-flex flex-col items-center gap-[2px] w-[38px] cursor-pointer group">
      <div className="relative w-[26px] h-[32px] rounded-[3px] border border-[var(--blt)] bg-[var(--white)] flex items-center justify-center transition-all group-hover:border-[var(--accent)] group-hover:-translate-y-[1px]">
        <div className="absolute top-0 right-0 w-[6px] h-[6px] bg-[var(--bg)] border-l border-b border-[var(--blt)] rounded-bl-[2px]" />
        <span
          className="text-[6px] font-bold uppercase tracking-wide text-white px-[2px] rounded-[1px]"
          style={{
            fontFamily: "var(--font-mono)",
            backgroundColor: extColor(name),
          }}
        >
          {extLabel(name)}
        </span>
      </div>
      <span
        className="max-w-[38px] text-center leading-[1.25]"
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: "8px",
          color: dark ? "var(--card-t2)" : "var(--t2)",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical" as any,
          overflow: "hidden",
          wordBreak: "break-all" as any,
        }}
      >
        {name}
      </span>
    </div>
  );
}

export function UploadButton() {
  return (
    <div className="upload-debug w-[26px] h-[26px] rounded-[3px] border border-dashed border-[var(--blt)] flex items-center justify-center text-[var(--t4)] cursor-pointer transition-all hover:border-[var(--accent)] hover:text-[var(--accent)] hover:bg-[var(--accent-light)]">
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <polyline points="16 16 12 12 8 16" />
        <line x1="12" y1="12" x2="12" y2="21" />
        <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
      </svg>
    </div>
  );
}
